import Portrait from '../Portrait'
import { displayPosition, positionFill } from '../board/positions'
import { recentPicks } from '../../lib/draftView'
import type { DraftState, PlayerSummary } from '../../types/protocol'

// Replaces the old PicksList, which rendered `Player #{pick.player_id}` — the
// raw database id. Every made pick read as "R1.3 - Alpha: Player #4034". The
// map it needed was already being built one scope up and just never passed in.

const MADE_BY_LABELS: Record<string, string> = {
  commissioner: 'Commissioner',
  auto: 'Autopick',
}

export default function RecentPicks({
  draft,
  playersById,
  limit = 12,
}: {
  draft: DraftState
  playersById: Map<number, PlayerSummary>
  limit?: number
}) {
  const picks = recentPicks(draft.picks, limit)

  return (
    <section className="panel p-4">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="eyebrow">Recent picks</h2>
        <p className="font-mono text-[11px] text-ink-lo tabular-nums">
          {draft.picks.filter((pick) => pick.status === 'made').length} made
        </p>
      </div>

      {picks.length === 0 ? (
        <p className="mt-3 text-sm text-ink-lo">No picks yet.</p>
      ) : (
        <ol className="mt-3 space-y-1">
          {picks.map((pick) => {
            const team = draft.teams.find((t) => t.id === pick.team_id)
            const player = pick.player_id != null ? playersById.get(pick.player_id) : undefined
            const label = pick.made_by ? MADE_BY_LABELS[pick.made_by] : null

            return (
              <li
                key={pick.overall}
                className="flex items-center gap-2 rounded-md bg-ink/50 px-2 py-1.5"
              >
                <span className="w-8 shrink-0 font-mono text-[10.5px] text-ink-lo tabular-nums">
                  {pick.round}.{pick.slot}
                </span>
                {player && (
                  <Portrait
                    fullName={player.full_name}
                    position={player.position}
                    nflTeam={player.nfl_team}
                    src={player.portrait_url}
                    size="xs"
                  />
                )}
                {player && (
                  <span
                    className={`${positionFill(player.position)} text-pos-ink w-7 shrink-0 rounded-sm py-0.5 text-center font-mono text-[9.5px] font-semibold`}
                  >
                    {displayPosition(player.position)}
                  </span>
                )}
                <span className="min-w-0 flex-1 truncate text-sm text-ink-hi">
                  {player?.full_name ?? `Player #${pick.player_id}`}
                </span>
                <span
                  className="max-w-20 shrink-0 truncate text-xs text-ink-lo"
                  title={label ? `${team?.name ?? '?'} · ${label}` : team?.name}
                >
                  {team?.name ?? '?'}
                  {label && <span className="text-ink-lo"> · {label}</span>}
                </span>
              </li>
            )
          })}
        </ol>
      )}
    </section>
  )
}
